import type { UploadMessages, UploadVariant } from './types';

/** A ready-made `<Upload>` configuration for one upload zone. */
export type UploadPreset = {
  variant: UploadVariant;
  /** HTML accept string — used for the picker hint AND `isFileAccepted` enforcement. */
  accept: string;
  maxSizeMb: number;
  maxCount: number;
  /** This zone's own copy — see `UploadMessages`. */
  messages: UploadMessages;
};

/**
 * Shared upload-zone presets. Single source for the D3 / story #102085 constraints
 * and each zone's copy. Spread into `<Upload>` / `<UploadField>`:
 *   <Upload {...UPLOAD_PRESETS.componentImages} onUpload={uploader} />
 */
export const UPLOAD_PRESETS: Record<'componentImages' | 'installationGuide', UploadPreset> = {
  componentImages: {
    variant: 'image',
    accept: 'image/jpeg,image/png,.jpg,.jpeg,.png',
    maxSizeMb: 5,
    maxCount: 5,
    // US-113319 AC-exact copy. AC owns the words, Figma owns the layout (ADR-0019, grilling Q9):
    // the newline is Figma node 422:363709's two-line dropzone, rendered via `white-space: pre-line`.
    messages: {
      help: 'Add up to 5 images (JPEG, JPG or PNG)\nmax 5MB each',
      invalidType: 'Only JPEG, JPG, or PNG files are supported',
      tooLarge: 'File size must not exceed 5MB',
      tooMany: 'You can upload a maximum of 5 images',
      // Figma 430:405319 reads "Upload more images (JPEG, JPGG or PNG)"; the AC owns copy and the
      // typo loses (ADR-0019, grilling Q9), exactly as it does in `help`.
      uploadMore: 'Upload more images (JPEG, JPG or PNG)',
    },
  },
  installationGuide: {
    variant: 'document',
    accept:
      'video/mp4,video/quicktime,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.mp4,.mov,.pdf,.doc,.docx',
    maxSizeMb: 100,
    maxCount: 5,
    messages: {
      help: 'Add up to 5 files (Video/PDF/DOC)\nMax 100MB each',
      invalidType: 'Only Video, PDF or DOC files are supported',
      tooLarge: 'File size must not exceed 100MB',
      tooMany: 'You can upload a maximum of 5 files',
      uploadMore: 'Upload more files (Video/PDF/DOC)',
    },
  },
};
